import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { useAuth } from "../../../hooks/useAuth";
import { useGiftees } from "../hooks/useGiftees";
import { useAllReminders } from "../hooks/useAllReminders";
import { useSavedRecommendations } from "../hooks/useSavedRecommendations";
import { openCreateGifteeWizard } from "../createGifteeWizardNav.js";
import { getBudgetMidpointDollars } from "../utils/formatBudget";
import { recommendationCardsForGiftee } from "../utils/giftSuggestions.js";
import { Button, Tags } from "../../../components/ui";
import GuestSaveBanner from "../../auth/components/GuestSaveBanner.jsx";
import GifteeFlowHeader from "../components/GifteeFlowHeader.jsx";
import GifteeAppBottomNav from "../components/GifteeAppBottomNav.jsx";
import GifteeEventRow from "../components/GifteeEventRow.jsx";
import ReminderRecoCarousel from "../components/ReminderRecoCarousel.jsx";
import AmazonAssociateFooter from "../components/AmazonAssociateFooter.jsx";
import {
  getNextUsHolidayOccurrence,
  getUsHolidayPreset,
} from "../data/usHolidayPresets.js";
import styles from "../styles/GifteeHome.module.css";

/** Figma Other Events rows — canonical IDs; dates resolve per calendar year. */
const OTHER_EVENT_PRESET_IDS = ["christmas", "thanksgiving", "easter", "halloween"];

const WINDOW_OPTIONS = [7, 14, 30, 60];

function firstNameFromUser(user) {
  if (!user) return "there";
  const raw =
    (typeof user.displayName === "string" && user.displayName.trim()) ||
    (typeof user.email === "string" && user.email.split("@")[0]) ||
    "there";
  const first = raw.split(/\s+/)[0];
  return first || "there";
}

function startOfDayMs(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
}

function reminderDayMs(rem) {
  if (!rem?.date) return null;
  const d = rem.date.toDate ? rem.date.toDate() : new Date(rem.date);
  if (Number.isNaN(d.getTime())) return null;
  return startOfDayMs(d);
}

function daysUntil(ts) {
  if (!ts) return null;
  const dateObj = ts.toDate ? ts.toDate() : new Date(ts);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const target = new Date(dateObj);
  target.setHours(0, 0, 0, 0);
  const diff = Math.ceil((target - now) / 86400000);
  if (diff < 0) {
    const nextYear = new Date(
      now.getFullYear() + 1,
      target.getMonth(),
      target.getDate(),
    );
    return Math.ceil((nextYear - now) / 86400000);
  }
  return diff;
}

/**
 * Reminders whose calendar day falls in [today, today + windowDays], same rule as `countRemindersInNextDays`.
 */
function remindersInWindowSorted(allReminders, windowDays) {
  const today = startOfDayMs(new Date());
  const end = today + windowDays * 86400000;
  return allReminders
    .filter((r) => {
      const t = reminderDayMs(r);
      if (t === null) return false;
      return t >= today && t <= end;
    })
    .sort((a, b) => {
      const da = reminderDayMs(a);
      const db = reminderDayMs(b);
      if (da !== db) return da - db;
      return String(a.gifteeId || "").localeCompare(String(b.gifteeId || ""));
    });
}

/** One row per giftee: soonest in-window reminder for that giftee (list is already sorted). */
function feedRemindersFromDistinctGiftees(sortedInWindow) {
  const seen = new Set();
  const out = [];
  for (const r of sortedInWindow) {
    if (!r.gifteeId || seen.has(r.gifteeId)) continue;
    seen.add(r.gifteeId);
    out.push(r);
  }
  return out;
}

function occasionTitleCase(rem) {
  if (!rem) return "";
  if (rem.type === "birthday") return "Birthday";
  if (rem.type === "anniversary") return "Anniversary";
  if (typeof rem.title === "string" && rem.title.trim()) {
    const t = rem.title.trim();
    return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
  }
  const raw = String(rem.type || "Occasion").replace(/-/g, " ");
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

function countRemindersInNextDays(allReminders, windowDays) {
  const today = startOfDayMs(new Date());
  const end = today + windowDays * 86400000;
  return allReminders.filter((r) => {
    const t = reminderDayMs(r);
    if (t === null) return false;
    return t >= today && t <= end;
  }).length;
}

function countGifteesForPresetType(allReminders, type) {
  const ids = new Set();
  for (const r of allReminders) {
    if (r.type === type && r.gifteeId) ids.add(r.gifteeId);
  }
  return ids.size;
}

function gifteesForPresetType(allReminders, giftees, type) {
  const ids = new Set();
  for (const r of allReminders) {
    if (r.type === type && r.gifteeId) ids.add(r.gifteeId);
  }
  return giftees.filter((g) => ids.has(g.id));
}


function avgBudgetMidpoint(giftees) {
  if (!giftees.length) return 0;
  const sum = giftees.reduce(
    (acc, g) => acc + getBudgetMidpointDollars(g),
    0,
  );
  return Math.round(sum / giftees.length);
}

/**
 * One giftee row on the home feed (Figma `Celebration Recommendations`).
 * Own `useSavedRecommendations` hook — stable per mounted card.
 */
function HomeFeedOccasionCard({
  reminder,
  giftee,
  gifteeName,
  gifteeId,
  expanded,
  headerIcon,
  onHeaderIconClick,
  daysLabel,
  occasionLabel,
  navigate,
}) {
  const { savedRecommendations, loading } = useSavedRecommendations(gifteeId);

  const recommendations = useMemo(
    () => recommendationCardsForGiftee(giftee, savedRecommendations),
    [giftee, savedRecommendations],
  );

  const recLabel = loading
    ? "…"
    : `${recommendations.length} Recommendations`;

  return (
    <section
      className={`${styles.feedCard} ${expanded ? "" : styles.feedCardCollapsed}`}
      aria-label={gifteeName}
    >
      <div className={styles.feedHead}>
        <button
          type="button"
          className={styles.personNameBtn}
          onClick={() => {
            if (!gifteeId) return;
            navigate(`/giftee/${gifteeId}`);
          }}
          aria-label={`View ${gifteeName}'s hub`}
          disabled={!gifteeId}
        >
          <h2 className={styles.personName}>{gifteeName}</h2>
        </button>
        {headerIcon === "ghost" ? (
          <span className={styles.iconGhost} aria-hidden="true">
            <i className="fa-solid fa-down-left-and-up-right-to-center" />
          </span>
        ) : (
          <button
            type="button"
            className={styles.iconBtn}
            onClick={onHeaderIconClick}
            aria-label={
              expanded
                ? "Collapse recommendations"
                : "Expand recommendations"
            }
          >
            {expanded ? (
              <i className="fa-solid fa-down-left-and-up-right-to-center" />
            ) : (
              <i className="fa-solid fa-up-right-and-down-left-from-center" />
            )}
          </button>
        )}
      </div>
      <div className={styles.tagRow}>
        <Tags label={occasionLabel} color="midnight" variant="filled" />
        <Tags label={daysLabel} color="brandPrimary" variant="filled" />
        <Tags label={recLabel} color="white" variant="filled" />
      </div>
      {expanded ? (
        loading ? (
          <p className={styles.carouselLoading}>Loading recommendations…</p>
        ) : (
          <ReminderRecoCarousel
            reminder={reminder}
            recommendations={recommendations}
            gifteeId={gifteeId}
            navigate={navigate}
            includeTopRecoPill
            showDots
          />
        )
      ) : null}
    </section>
  );
}

export default function GifteeHomeScreen() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { giftees, loading: gifteesLoading } = useGiftees();
  const { allReminders, loading: remindersLoading } = useAllReminders(giftees);

  const [windowDays, setWindowDays] = useState(30);
  const [isWindowMenuOpen, setIsWindowMenuOpen] = useState(false);
  /** Which feed row shows the reco carousel. `null` means all rows collapsed. */
  const [expandedIndex, setExpandedIndex] = useState(0);
  const [expandedOtherEventKey, setExpandedOtherEventKey] = useState(null);
  const windowMenuRef = useRef(null);

  const windowRemindersSorted = useMemo(
    () => remindersInWindowSorted(allReminders, windowDays),
    [allReminders, windowDays],
  );

  const feedReminders = useMemo(
    () => feedRemindersFromDistinctGiftees(windowRemindersSorted),
    [windowRemindersSorted],
  );

  const feedKey = useMemo(
    () =>
      feedReminders
        .map((r, i) => `${r.gifteeId ?? ""}:${r.id ?? `i${i}`}`)
        .join("|"),
    [feedReminders],
  );

  useEffect(() => {
    setExpandedIndex(0);
  }, [feedKey]);

  useEffect(() => {
    if (!isWindowMenuOpen) return undefined;
    const handlePointerDown = (event) => {
      if (!windowMenuRef.current?.contains(event.target)) {
        setIsWindowMenuOpen(false);
      }
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [isWindowMenuOpen]);


  useEffect(() => {
    if (gifteesLoading) return;
    if (giftees.length === 0) {
      openCreateGifteeWizard(navigate, "/home", { replace: true });
    }
  }, [gifteesLoading, giftees.length, navigate]);

  const giftCount = useMemo(
    () => countRemindersInNextDays(allReminders, windowDays),
    [allReminders, windowDays],
  );

  const welcomeName = firstNameFromUser(currentUser);
  const budgetAvg = useMemo(() => avgBudgetMidpoint(giftees), [giftees]);

  const otherEventRows = useMemo(
    () =>
      OTHER_EVENT_PRESET_IDS.map((type) => {
        const preset = getUsHolidayPreset(type);
        const d = getNextUsHolidayOccurrence(type);
        const n = countGifteesForPresetType(allReminders, type);
        const dayMs = startOfDayMs(d);
        const dUntil = daysUntil(d);
        const eventGiftees = gifteesForPresetType(allReminders, giftees, type);
        return {
          key: type,
          title: preset?.name || "Holiday",
          gifteeCount: n,
          giftees: eventGiftees,
          dateLabel: `${d.getMonth() + 1}/${d.getDate()}`,
          daysLabel:
            dUntil != null && Number.isFinite(dUntil)
              ? `${dUntil} days`
              : "—",
          sort: dayMs,
        };
      })
      .filter((row) => row.gifteeCount > 0)
      .sort((a, b) => a.sort - b.sort),
    [allReminders, giftees],
  );


  if (gifteesLoading || remindersLoading) {
    return (
      <div className={styles.shell}>
        <GifteeFlowHeader
          welcomeHeadline={`Welcome, ${firstNameFromUser(currentUser)}!`}
          showClose={false}
        />
        <p className={styles.loading}>Loading...</p>
      </div>
    );
  }

  // Reachable by dismissing the wizard before saving anyone — guests land here
  // right after starting a trial, so offer the way back in instead of a blank page.
  if (!giftees.length) {
    return (
      <div className={styles.shell}>
        <main className={styles.scrollMain}>
          <GifteeFlowHeader
            welcomeHeadline={`Welcome, ${welcomeName}!`}
            showClose={false}
          />
          <div className={styles.contentStack}>
            <GuestSaveBanner />
            <p className={styles.loading}>
              No giftees yet — add someone to get gift ideas and reminders.
            </p>
            <div className={styles.addGifteeRow}>
              <Button
                variant="primary"
                size="md"
                layout="textIcon"
                fullWidth
                label="Add Giftee"
                icon={<i className="fa-solid fa-plus" aria-hidden />}
                onClick={() => openCreateGifteeWizard(navigate, "/home")}
              />
            </div>
          </div>
        </main>
        <GifteeAppBottomNav />
      </div>
    );
  }

  return (
    <div className={styles.shell}>
      <main className={styles.scrollMain}>
        <GifteeFlowHeader
          welcomeHeadline={`Welcome, ${welcomeName}!`}
          showClose={false}
        />
        <div className={styles.contentStack}>
          <GuestSaveBanner />

          {/* Frame 289 — summary */}
          <div className={styles.summaryBlock}>
            <div className={styles.summaryRow}>
              <span>You have</span>
              <span className={styles.numChip}>{giftCount}</span>
              <span>{giftCount === 1 ? "gift" : "gifts"}</span>
            </div>
            <div className={`${styles.summaryRow} ${styles.summaryRowSecond}`}>
              <span>in</span>
              <span>the</span>
              <span>next</span>
              <div className={styles.dayWindowChipSlot} ref={windowMenuRef}>
                <div
                  className={`${styles.dayWindowChip} ${isWindowMenuOpen ? styles.dayWindowChipOpen : ""}`}
                >
                  <button
                    id="home-window-days"
                    type="button"
                    className={styles.dayWindowSelect}
                    aria-label="Days ahead"
                    aria-haspopup="listbox"
                    aria-expanded={isWindowMenuOpen}
                    onClick={() => setIsWindowMenuOpen((prev) => !prev)}
                  >
                    {windowDays}
                    <span className={styles.dayWindowChevron} aria-hidden="true">
                      <svg viewBox="0 0 20 20" focusable="false">
                        <path d="M6 8.5L10 12.5L14 8.5" />
                      </svg>
                    </span>
                  </button>
                  {isWindowMenuOpen ? (
                    <div className={styles.dayWindowOptions} role="listbox">
                      {WINDOW_OPTIONS.map((n) => (
                        <button
                          key={n}
                          type="button"
                          className={styles.dayWindowOption}
                          role="option"
                          aria-selected={windowDays === n}
                          onClick={() => {
                            setWindowDays(n);
                            setIsWindowMenuOpen(false);
                          }}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
            <div className={`${styles.summaryRow} ${styles.summaryRowThird}`}>
              <span>days.</span>
            </div>
            <div className={styles.addGifteeRow}>
              <Button
                variant="primary"
                size="md"
                layout="textIcon"
                fullWidth
                label="Add Giftee"
                icon={<i className="fa-solid fa-plus" aria-hidden />}
                onClick={() => openCreateGifteeWizard(navigate, "/home")}
              />
            </div>
          </div>

          {/* Frame 284 — per-giftee cards */}
          <div className={styles.feedStack}>
            {feedReminders.length === 0 ? (
              <p className={styles.loading}>No upcoming occasions yet.</p>
            ) : (
              feedReminders.map((reminder, idx) => {
                const giftee = giftees.find((g) => g.id === reminder.gifteeId);
                const name = giftee?.name || "Giftee";
                const d = daysUntil(reminder.date);
                const daysLabel =
                  d != null && Number.isFinite(d)
                    ? `${d} ${d === 1 ? "day" : "days"}`
                    : "—";
                const expanded = idx === expandedIndex;
                return (
                  <HomeFeedOccasionCard
                    key={`${reminder.gifteeId ?? "g"}-${reminder.id ?? `idx-${idx}`}`}
                    reminder={reminder}
                    giftee={giftee}
                    gifteeName={name}
                    gifteeId={reminder.gifteeId}
                    expanded={expanded}
                    occasionLabel={occasionTitleCase(reminder)}
                    daysLabel={daysLabel}
                    headerIcon="toggle"
                    onHeaderIconClick={() => {
                      if (expanded) {
                        setExpandedIndex(null);
                      } else {
                        setExpandedIndex(idx);
                      }
                    }}
                    navigate={navigate}
                  />
                );
              })
            )}
          </div>

          {/* Frame 290 — Other events */}
          <section className={styles.otherSection} aria-label="Other events">
            <h2 className={styles.otherTitle}>Other Events</h2>
            <div className={styles.otherList}>
              {otherEventRows.map((row) => {
                const isExpanded = expandedOtherEventKey === row.key;
                return (
                  <article
                    key={row.key}
                    className={`${styles.otherCard} ${isExpanded ? styles.otherCardExpanded : ""}`}
                  >
                    <div className={styles.otherHead}>
                      <h3 className={styles.otherEventTitle}>{row.title}</h3>
                      <button
                        type="button"
                        className={styles.iconBtn}
                        onClick={() =>
                          setExpandedOtherEventKey(
                            isExpanded ? null : row.key,
                          )
                        }
                        aria-label={
                          isExpanded
                            ? `Collapse ${row.title}`
                            : `Expand ${row.title}`
                        }
                      >
                        {isExpanded ? (
                          <i className="fa-solid fa-down-left-and-up-right-to-center" />
                        ) : (
                          <i className="fa-solid fa-up-right-and-down-left-from-center" />
                        )}
                      </button>
                    </div>
                    <div className={styles.tagRow}>
                      {isExpanded ? (
                        <>
                          <Tags
                            label={row.dateLabel}
                            color="brandPrimary"
                            variant="filled"
                          />
                          <Tags
                            label={row.daysLabel}
                            color="brandPrimary"
                            variant="filled"
                          />
                          <Tags
                            label={`${row.gifteeCount} Giftees`}
                            color="white"
                            variant="filled"
                          />
                        </>
                      ) : (
                        <>
                          <Tags
                            label={`${row.gifteeCount} Giftees`}
                            color="midnight"
                            variant="filled"
                          />
                          <Tags
                            label={row.dateLabel}
                            color="brandPrimary"
                            variant="filled"
                          />
                          <Tags
                            label={row.daysLabel}
                            color="brandPrimary"
                            variant="filled"
                          />
                        </>
                      )}
                    </div>
                    {isExpanded && row.giftees.length > 0 && (
                      <div className={styles.otherGifteeList}>
                        {row.giftees.map((giftee) => (
                          <GifteeEventRow
                            key={giftee.id}
                            giftee={giftee}
                            navigate={navigate}
                          />
                        ))}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </section>

          {/* Frame 301 / inner card Figma 420:15703 */}
          <div className={styles.accountOuter}>
            <div className={styles.accountCard}>
              <div className={styles.accountHead}>
                <h2 className={styles.accountTitle}>Account Overview</h2>
                <button
                  type="button"
                  className={styles.accountEdit}
                  aria-label="Manage giftees"
                  onClick={() => navigate("/all-giftees")}
                >
                  <i className="fa-solid fa-pen-to-square" />
                </button>
              </div>

              <div className={styles.accountStack}>
                <hr className={styles.accountRule} />

                <div className={styles.accountSection}>
                  <button
                    type="button"
                    className={styles.accountStatRow}
                    onClick={() => navigate("/all-giftees")}
                  >
                    <span className={styles.metricBox}>
                      <span className={styles.metricBoxText}>
                        {giftees.length}
                      </span>
                    </span>
                    <span className={styles.metricLabel}>Giftees</span>
                  </button>
                  <button
                    type="button"
                    className={styles.accountAddBtn}
                    onClick={() => openCreateGifteeWizard(navigate, "/home")}
                  >
                    Add New
                    <i className="fa-solid fa-plus" aria-hidden />
                  </button>
                </div>

                <hr className={styles.accountRule} />

                <div className={styles.accountSection}>
                  <button
                    type="button"
                    className={styles.accountStatCol}
                    onClick={() => navigate("/all-occasions")}
                  >
                    <span className={styles.metricBox}>
                      <span className={styles.metricBoxText}>
                        {allReminders.length}
                      </span>
                    </span>
                    <span className={styles.metricLabel}>Celebrations</span>
                  </button>
                  <button
                    type="button"
                    className={styles.accountAddBtn}
                  >
                    Add New
                    <i className="fa-solid fa-plus" aria-hidden />
                  </button>
                </div>

                <hr className={styles.accountRule} />

                <div className={styles.accountSection}>
                  <div className={styles.accountBudgetRow}>
                    <span className={styles.metricBox}>
                      <span className={styles.metricBoxText}>
                        ${budgetAvg}
                      </span>
                    </span>
                    <p className={styles.metricLabel}>
                      <span>Budget</span>
                      <span className={styles.metricLabelSuffix}> AVG.</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <AmazonAssociateFooter />
      </main>
      <GifteeAppBottomNav />
    </div>
  );
}
